import React, { useState, useEffect, useMemo } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords, useHmsConfig } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   DIALYSIS MODULE — Complete Renal Care
   - Patient Registration & ESRD Profile
   - Session Scheduling & Machine Allocation
   - Intra-Dialysis Monitoring (vitals, events)
   - HD Parameters (UF, QB, QD, conductivity)
   - Dialysis Access (AV Fistula/Graft/CVC)
   - Medication During Dialysis
   - Monthly Adequacy (Kt/V)
   - Machine Management
═══════════════════════════════════════════════════════════ */

function MachinesSetup({ onSave, busy }) {
  const [rows, setRows] = useState([{ id: 'HD-01', model: '', station: 'Station 1' }]);
  return (
    <div className="card" style={{ borderLeft: '4px solid #3b82f6', marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, color: '#2563eb' }}>Configure Dialysis Machines</h3>
      <p className="hint mb">No machines configured yet. Add your HD units to enable session scheduling.</p>
      {rows.map((r, i) => (
        <div key={i} className="form-grid mb" style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 10 }}>
          <div className="field"><label>Machine ID</label><input className="input" value={r.id} onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, id: e.target.value } : x))} /></div>
          <div className="field"><label>Model</label><input className="input" value={r.model} onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, model: e.target.value } : x))} placeholder="e.g. Fresenius 5008S" /></div>
          <div className="field"><label>Station</label><input className="input" value={r.station} onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, station: e.target.value } : x))} /></div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRows((list) => [...list, { id: `HD-${String(list.length + 1).padStart(2, '0')}`, model: '', station: `Station ${list.length + 1}` }])}>+ Add Row</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => onSave(rows.map((r) => ({ ...r, status: 'Available', patient: null })))}>{busy ? 'Saving…' : '✓ Save Machines'}</button>
      </div>
    </div>
  );
}

function SessionForm({ patient, machines, onStartSession }) {
  const [params, setParams] = useState({ machine: machines[0]?.id || 'HD-01', preWeight: '', preSpO2: 98, preBP: '', preHR: 75, ufGoal: '', qb: 300, qd: 500, conductivity: 14.0, sodium: 138, dialyzer: 'FX80', anticoag: 'Heparin 2000 IU', duration: 240, nurse: '' });
  const [vitals, setVitals] = useState([{ time: '00:00', bp: '120/80', hr: 72, temp: 36.8, spO2: 98, uf: 0, note: '' }]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const ufNeeded = patient ? ((parseFloat(params.preWeight) - patient.dw) * 1000).toFixed(0) : 0;

  function addVitals() {
    setVitals(v => [...v, { time: `${String(Math.floor(vitals.length * 30 / 60)).padStart(2, '0')}:${String((vitals.length * 30) % 60).padStart(2, '0')}`, bp: '', hr: '', temp: '', spO2: '', uf: 0, note: '' }]);
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card" style={{ borderLeft: '4px solid #3b82f6' }}>
          <h4 style={{ marginTop: 0, color: '#2563eb' }}>🔧 HD Parameters</h4>
          <div className="form-grid">
            <div className="field"><label>Machine</label>
              <select className="input" value={params.machine} onChange={e => setParams(p => ({ ...p, machine: e.target.value }))}>
                {machines.filter(m => m.status === 'Available' || m.patient === patient?.name).map(m => <option key={m.id} value={m.id}>{m.id} — {m.model}</option>)}
              </select>
            </div>
            <div className="field"><label>Pre-Dialysis Weight (kg)</label><input type="number" step="0.1" className="input" value={params.preWeight} onChange={e => setParams(p => ({ ...p, preWeight: e.target.value }))} /></div>
            <div className="field"><label>Dry Weight (kg)</label><input type="number" className="input" value={patient?.dw || ''} readOnly style={{ background: '#f8fafc' }} /></div>
            <div className="field"><label style={{ color: '#f97316' }}>UF Required (mL)</label><input className="input" value={ufNeeded > 0 ? ufNeeded : params.ufGoal} readOnly={ufNeeded > 0} onChange={e => setParams(p => ({ ...p, ufGoal: e.target.value }))} style={{ fontWeight: 800, color: '#f97316' }} /></div>
            <div className="field"><label>QB — Blood Flow (mL/min)</label><input type="number" className="input" value={params.qb} onChange={e => setParams(p => ({ ...p, qb: e.target.value }))} /></div>
            <div className="field"><label>QD — Dialysate Flow (mL/min)</label><input type="number" className="input" value={params.qd} onChange={e => setParams(p => ({ ...p, qd: e.target.value }))} /></div>
            <div className="field"><label>Conductivity (mS/cm)</label><input type="number" step="0.1" className="input" value={params.conductivity} onChange={e => setParams(p => ({ ...p, conductivity: e.target.value }))} /></div>
            <div className="field"><label>Na (mEq/L)</label><input type="number" className="input" value={params.sodium} onChange={e => setParams(p => ({ ...p, sodium: e.target.value }))} /></div>
            <div className="field"><label>Dialyzer</label><select className="input" value={params.dialyzer} onChange={e => setParams(p => ({ ...p, dialyzer: e.target.value }))}><option>FX80</option><option>FX100</option><option>HF80S</option><option>Revaclear 300</option><option>Revaclear Max</option></select></div>
            <div className="field"><label>Anticoagulation</label><select className="input" value={params.anticoag} onChange={e => setParams(p => ({ ...p, anticoag: e.target.value }))}><option>Heparin 2000 IU</option><option>Heparin-free</option><option>LMWH</option><option>Citrate Anticoagulation</option></select></div>
            <div className="field"><label>Duration (min)</label><input type="number" className="input" value={params.duration} onChange={e => setParams(p => ({ ...p, duration: e.target.value }))} /></div>
            <div className="field"><label>Nurse In-Charge</label><input className="input" value={params.nurse} onChange={e => setParams(p => ({ ...p, nurse: e.target.value }))} /></div>
          </div>
          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn btn-primary" disabled={busy || !patient} onClick={async () => {
              if (!onStartSession || !patient) return;
              setBusy(true);
              try {
                await onStartSession({ patientId: patient.id || patient.recordId, patientName: patient.name, params, vitals });
                setSaved(true);
              } finally { setBusy(false); }
            }}>{busy ? 'Saving…' : '▶ Start Session'}</button>
            {saved && <span style={{ marginLeft: 10, color: '#10b981', fontWeight: 700 }}>✓ Session Started!</span>}
          </div>
        </div>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <h4 style={{ margin: 0, color: '#f97316' }}>📊 Intra-Dialysis Vitals</h4>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addVitals}>+ Add Reading</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
              <thead>
                <tr style={{ background: '#1e293b', color: '#fff' }}>
                  {['Time', 'BP', 'HR', 'SpO₂%', 'UF Done', 'Notes'].map(h => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vitals.map((v, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                    <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 700 }}>{v.time}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'center' }}><input className="input" style={{ width: 70, padding: '2px 6px', fontSize: 11 }} value={v.bp} onChange={e => setVitals(arr => arr.map((x, j) => j === i ? { ...x, bp: e.target.value } : x))} placeholder="120/80" /></td>
                    <td style={{ padding: '5px 8px', textAlign: 'center' }}><input type="number" className="input" style={{ width: 50, padding: '2px 6px', fontSize: 11 }} value={v.hr} onChange={e => setVitals(arr => arr.map((x, j) => j === i ? { ...x, hr: e.target.value } : x))} /></td>
                    <td style={{ padding: '5px 8px', textAlign: 'center' }}><input type="number" className="input" style={{ width: 50, padding: '2px 6px', fontSize: 11 }} value={v.spO2} onChange={e => setVitals(arr => arr.map((x, j) => j === i ? { ...x, spO2: e.target.value } : x))} /></td>
                    <td style={{ padding: '5px 8px', textAlign: 'center' }}><input type="number" className="input" style={{ width: 55, padding: '2px 6px', fontSize: 11 }} value={v.uf} onChange={e => setVitals(arr => arr.map((x, j) => j === i ? { ...x, uf: e.target.value } : x))} /></td>
                    <td style={{ padding: '5px 8px' }}><input className="input" style={{ width: 100, padding: '2px 6px', fontSize: 11 }} value={v.note} onChange={e => setVitals(arr => arr.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} placeholder="Event / note" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#f0fdf4', borderRadius: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>Patient Profile</div>
            {[['ESRD Cause', patient?.diagnosis], ['Access', patient?.access], ['Blood Group', patient?.blood], ['Nephrologist', patient?.nephrologist], ['Last Kt/V', patient?.ktv]].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#64748b' }}>{label}</span><span style={{ fontWeight: 700 }}>{val || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DialysisPage() {
  const { records: patients, create: createPatient, loading: patientsLoading } = useHmsRecords('dialysis-patient');
  const { records: sessions, create: createSession, patch: patchSession } = useHmsRecords('dialysis-session');
  const { config, save: saveMachines, loading: machinesLoading } = useHmsConfig('dialysis-machines');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [savingMachines, setSavingMachines] = useState(false);
  const [showReg, setShowReg] = useState(false);
  const [regBusy, setRegBusy] = useState(false);
  const [regMsg, setRegMsg] = useState('');
  const [endingId, setEndingId] = useState('');
  const [regForm, setRegForm] = useState({
    name: '', diagnosis: 'ESRD', access: 'AV Fistula Left', blood: 'O+', nephrologist: '',
    frequency: 'Thrice weekly', ktv: '1.2',
  });

  const machines = config?.machines || [];

  useEffect(() => {
    if (patients.length && !selectedPatient) setSelectedPatient(patients[0]);
  }, [patients, selectedPatient]);

  const patientList = patients;

  const sessionsToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return sessions.filter((s) => (s.startedAt || '').slice(0, 10) === today).length;
  }, [sessions]);

  const activeSessions = useMemo(
    () => sessions.filter((s) => s.status === 'In Progress'),
    [sessions],
  );

  const startSession = async ({ patientId, patientName, params, vitals }) => {
    const machine = machines.find((m) => m.id === params.machine);
    if (machine && machine.status === 'In Use' && machine.patient !== patientName) {
      throw new Error(`Machine ${params.machine} is already in use`);
    }
    await createSession({
      patientId,
      patientName,
      params,
      vitals,
      machineId: params.machine,
      status: 'In Progress',
      startedAt: new Date().toISOString(),
    });
    const nextMachines = machines.map((m) => (
      m.id === params.machine ? { ...m, status: 'In Use', patient: patientName } : m
    ));
    await saveMachines({ machines: nextMachines });
  };

  const endSession = async (session) => {
    const id = session.recordId || session.id;
    if (!id) return;
    setEndingId(id);
    try {
      const postWeight = window.prompt('Post-dialysis weight (kg) — optional', '') || '';
      await patchSession(id, {
        status: 'Completed',
        endedAt: new Date().toISOString(),
        postWeight: postWeight ? Number(postWeight) : null,
      });
      const machineId = session.machineId || session.params?.machine;
      if (machineId) {
        const nextMachines = machines.map((m) => (
          m.id === machineId ? { ...m, status: 'Available', patient: null } : m
        ));
        await saveMachines({ machines: nextMachines });
      }
    } catch (err) {
      window.alert(err.message || 'Could not end session');
    } finally {
      setEndingId('');
    }
  };

  const statusColor = { Available: '#10b981', 'In Use': '#3b82f6', 'Under Maintenance': '#dc2626' };

  const handleSaveMachines = async (list) => {
    setSavingMachines(true);
    try {
      await saveMachines({ machines: list });
    } finally { setSavingMachines(false); }
  };

  const registerPatient = async (e) => {
    e.preventDefault();
    if (!regForm.name.trim()) { setRegMsg('Patient name is required.'); return; }
    setRegBusy(true);
    setRegMsg('');
    try {
      const created = await createPatient({
        ...regForm,
        ktv: Number(regForm.ktv || 0),
        status: 'Active',
      });
      setShowReg(false);
      setRegForm({ name: '', diagnosis: 'ESRD', access: 'AV Fistula Left', blood: 'O+', nephrologist: '', frequency: 'Thrice weekly', ktv: '1.2' });
      if (created) setSelectedPatient(created);
      setRegMsg('Dialysis patient registered.');
    } catch (err) {
      setRegMsg(err.message || 'Could not register patient');
    } finally {
      setRegBusy(false);
    }
  };

  return (
    <PageShell
      title="Dialysis Unit"
      subtitle="Hemodialysis Session Management · HD Parameters · Machine Allocation · Intra-Dialysis Monitoring"
      actions={(
        <button type="button" className="btn btn-primary" onClick={() => setShowReg((s) => !s)}>
          {showReg ? 'Cancel' : '+ Register Patient'}
        </button>
      )}
    >
      {regMsg && <div className={`mb ${/fail|error|could/i.test(regMsg) ? 'error' : 'info-banner'}`}>{regMsg}</div>}
      {showReg && (
        <form className="card mb" onSubmit={registerPatient} style={{ borderLeft: '4px solid #0d6e6a' }}>
          <h3 style={{ marginTop: 0 }}>Register Dialysis Patient</h3>
          <div className="form-grid mb">
            <div className="field"><label>Patient name *</label><input className="input" required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} /></div>
            <div className="field"><label>Diagnosis</label><input className="input" value={regForm.diagnosis} onChange={(e) => setRegForm({ ...regForm, diagnosis: e.target.value })} /></div>
            <div className="field"><label>Access</label><select className="input" value={regForm.access} onChange={(e) => setRegForm({ ...regForm, access: e.target.value })}>{['AV Fistula Left', 'AV Fistula Right', 'Permcath', 'Temporary catheter', 'Graft'].map((a) => <option key={a}>{a}</option>)}</select></div>
            <div className="field"><label>Blood group</label><select className="input" value={regForm.blood} onChange={(e) => setRegForm({ ...regForm, blood: e.target.value })}>{['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map((b) => <option key={b}>{b}</option>)}</select></div>
            <div className="field"><label>Nephrologist</label><input className="input" value={regForm.nephrologist} onChange={(e) => setRegForm({ ...regForm, nephrologist: e.target.value })} placeholder="Dr. Name" /></div>
            <div className="field"><label>Frequency</label><select className="input" value={regForm.frequency} onChange={(e) => setRegForm({ ...regForm, frequency: e.target.value })}>{['Thrice weekly', 'Twice weekly', 'Alternate day', 'Daily'].map((f) => <option key={f}>{f}</option>)}</select></div>
            <div className="field"><label>Last Kt/V</label><input type="number" step="0.1" className="input" value={regForm.ktv} onChange={(e) => setRegForm({ ...regForm, ktv: e.target.value })} /></div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={regBusy}>{regBusy ? 'Saving…' : 'Save Patient'}</button>
        </form>
      )}
      {!machinesLoading && machines.length === 0 && (
        <MachinesSetup onSave={handleSaveMachines} busy={savingMachines} />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Patients', val: patientList.length, color: '#6366f1' }, { label: 'Sessions Today', val: sessionsToday, color: '#3b82f6' }, { label: 'Machines In Use', val: machines.filter(m => m.status === 'In Use').length, color: '#f59e0b' }, { label: 'Available', val: machines.filter(m => m.status === 'Available').length, color: '#10b981' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Machine Status</div>
        {machines.length === 0 ? (
          <p className="hint">Configure machines above to see status board.</p>
        ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {machines.map(m => (
            <div key={m.id} style={{ padding: '8px 14px', borderRadius: 10, border: `2px solid ${(statusColor[m.status] || '#94a3b8')}40`, background: (statusColor[m.status] || '#94a3b8') + '10', textAlign: 'center', minWidth: 100 }}>
              <div style={{ fontFamily: 'monospace', fontWeight: 900, color: statusColor[m.status] || '#94a3b8', fontSize: 14 }}>{m.id}</div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{m.status === 'In Use' ? m.patient : m.status}</div>
            </div>
          ))}
        </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>ESRD Patients</div>
          {patientsLoading && <p className="hint">Loading…</p>}
          {!patientsLoading && patientList.length === 0 && (
            <p className="hint">No dialysis patients yet. Click “+ Register Patient”.</p>
          )}
          {patientList.map(p => (
            <div key={p.id || p.recordId} onClick={() => setSelectedPatient(p)} style={{ padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${selectedPatient?.id === p.id || selectedPatient?.recordId === p.recordId ? '#0d6e6a' : '#e2e8f0'}`, background: selectedPatient?.id === p.id || selectedPatient?.recordId === p.recordId ? '#f0fdfa' : '#fff', marginBottom: 8, transition: 'all 0.2s' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{p.frequency}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Access: {(p.access || '—').split(' ')[0]}</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Kt/V: <strong style={{ color: p.ktv >= 1.2 ? '#10b981' : '#dc2626', fontFamily: 'Outfit,sans-serif' }}>{p.ktv}</strong><span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>(target ≥1.2)</span></div>
            </div>
          ))}
        </div>
        <div>
          {activeSessions.length > 0 && (
            <div className="card mb" style={{ borderLeft: '4px solid #f59e0b' }}>
              <h4 style={{ marginTop: 0 }}>Active sessions</h4>
              {activeSessions.map((s) => (
                <div key={s.recordId || s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{s.patientName}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {s.machineId || s.params?.machine || '—'} · started {s.startedAt ? new Date(s.startedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={endingId === (s.recordId || s.id)}
                    onClick={() => endSession(s)}
                  >
                    {endingId === (s.recordId || s.id) ? 'Ending…' : 'End session'}
                  </button>
                </div>
              ))}
            </div>
          )}
          <SessionForm patient={selectedPatient} machines={machines} onStartSession={startSession} />
        </div>
      </div>
    </PageShell>
  );
}
